import { Link } from "react-router-dom";
import { SocialButton, InstagramIcon, LinkedInIcon, GitHubIcon } from "./ui/SocialButton";
import geekcowLogo from "../assets/geekcow-logo-full.png";
import { SOCIAL_LINKS, LEGAL } from "../lib/brand";

type FooterLink = { label: string; section?: string; to?: string; href?: string };

interface SiteFooterProps {
  /** Which face this footer sits under — drives the tagline and the cross-link. */
  face: "agency" | "profile";
  blurb: string;
  navLinks: FooterLink[];
  onSectionClick: (sectionId: string) => void;
}

const LEGAL_LINKS: FooterLink[] = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms of Service", to: "/terms" },
  { label: "Cookies", to: "/cookies" },
];

export function SiteFooter({ face, blurb, navLinks, onSectionClick }: SiteFooterProps) {
  const crossLink =
    face === "agency"
      ? { to: "/olga", label: "Match and hire our founder Olga — see her profile" }
      : { to: "/", label: "Do you need more than a couple of hands? Meet the studio" };

  // The agency links the studio page; the profile links Olga's own.
  const isAgency = face === "agency";
  const linkedIn = isAgency
    ? { href: SOCIAL_LINKS.linkedinCompany, label: "Follow GeekCow Creative Studio on LinkedIn" }
    : { href: SOCIAL_LINKS.linkedin, label: "Connect with Olga on LinkedIn" };

  const renderLink = (link: FooterLink) => {
    const className =
      "text-[15px] leading-[28px] text-[#141414] hover:text-[#003e5b] hover:underline underline-offset-4 transition-colors text-left";
    if (link.to) {
      return (
        <Link key={link.label} to={link.to} className={className}>
          {link.label}
        </Link>
      );
    }
    if (link.href) {
      return (
        <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className={className}>
          {link.label}
        </a>
      );
    }
    return (
      <button key={link.label} type="button" onClick={() => onSectionClick(link.section!)} className={className}>
        {link.label}
      </button>
    );
  };

  return (
    <footer className="relative w-full px-4 md:px-[116px] pt-12 md:pt-16 pb-10">
      <div className="max-w-[1400px] mx-auto flex flex-col gap-8 md:gap-10">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 md:gap-10">
          {/* Brand */}
          <div className="flex flex-col gap-4 max-w-[360px]">
            {/* The full lockup, same as the "Who we are" section. */}
            <img src={geekcowLogo} alt="GeekCow Creative Studio" className="w-[190px] h-auto" />
            <p className="text-[15px] leading-[22px] text-[#141414]">{blurb}</p>
            <div className="flex gap-3 mt-1">
              <SocialButton href={linkedIn.href} ariaLabel={linkedIn.label}>
                <LinkedInIcon className="w-5 h-5" />
              </SocialButton>
              <SocialButton href={SOCIAL_LINKS.instagram} ariaLabel="Follow on Instagram">
                <InstagramIcon className="w-5 h-5" />
              </SocialButton>
              <SocialButton href={SOCIAL_LINKS.github} ariaLabel="View GitHub profile">
                <GitHubIcon className="w-5 h-5" />
              </SocialButton>
            </div>
          </div>

          {/* Link columns */}
          <div className="flex gap-12 md:gap-20">
            <nav className="flex flex-col gap-1" aria-label="Footer navigation">
              <p className="font-bold text-[14px] tracking-[0.5px] uppercase text-[#141414] mb-2">Navigation</p>
              {navLinks.map(renderLink)}
            </nav>
            {LEGAL_LINKS.length > 0 && (
              <nav className="flex flex-col gap-1" aria-label="Legal">
                <p className="font-bold text-[14px] tracking-[0.5px] uppercase text-[#141414] mb-2">Legal</p>
                {LEGAL_LINKS.map(renderLink)}
              </nav>
            )}
          </div>
        </div>

        {/* Cross-link between the two faces */}
        <Link
          to={crossLink.to}
          className="group flex w-full items-center justify-between gap-3 rounded-full border border-[#141414] px-5 py-3 text-[14px] font-medium text-[#141414] hover:bg-[#141414] hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#003e5b] md:w-auto md:self-start md:justify-start"
        >
          {crossLink.label}
          <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">
            →
          </span>
        </Link>

        <div className="w-full h-[1px] bg-[#141414]/15" />

        <p className="text-[13px] leading-[20px] text-[#666] text-center">
          © {new Date().getFullYear()} {LEGAL.entity} is a {LEGAL.form}
          <br />
          KvK# {LEGAL.kvk} — All rights reserved.
        </p>
      </div>
    </footer>
  );
}
